import { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n/context.js';
import { fitDistance, zoomLimits } from '../lib/model3dFraming.js';

export interface Model3DViewerProps {
  /** URL of a binary glTF (.glb) file. */
  url: string;
}

type ViewerState = 'loading' | 'ready' | 'error' | 'unsupported';

interface ViewerHandle {
  resetView: () => void;
  setAutoRotate: (enabled: boolean) => void;
}

/**
 * Interactive 3D viewer: drag to orbit, pinch / wheel to zoom, two fingers (or right
 * button) to pan. three.js is loaded on demand so it never weighs on the main bundle.
 */
export function Model3DViewer({ url }: Model3DViewerProps) {
  const { t } = useI18n();
  const hostRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<ViewerHandle | null>(null);
  const [state, setState] = useState<ViewerState>('loading');
  const [progress, setProgress] = useState(0);
  const [autoRotate, setAutoRotate] = useState(true);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    let disposed = false;
    let cleanup: (() => void) | null = null;
    setState('loading');
    setProgress(0);

    void (async () => {
      try {
        const [THREE, { GLTFLoader }, { OrbitControls }, { RoomEnvironment }] = await Promise.all([
          import('three'),
          import('three/addons/loaders/GLTFLoader.js'),
          import('three/addons/controls/OrbitControls.js'),
          import('three/addons/environments/RoomEnvironment.js'),
        ]);
        if (disposed) return;

        let renderer: InstanceType<typeof THREE.WebGLRenderer>;
        try {
          renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        } catch {
          setState('unsupported');
          return;
        }
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.domElement.className = 'model3d-canvas';
        host.appendChild(renderer.domElement);

        const scene = new THREE.Scene();
        const pmrem = new THREE.PMREMGenerator(renderer);
        const environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
        scene.environment = environment;
        const key = new THREE.DirectionalLight(0xffffff, 1.2);
        key.position.set(2, 4, 3);
        scene.add(key);

        const camera = new THREE.PerspectiveCamera(32, 1, 0.01, 1000);
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.08;
        controls.autoRotate = true;
        controls.autoRotateSpeed = 1.6;
        controls.screenSpacePanning = true;
        controls.addEventListener('start', () => {
          controls.autoRotate = false;
          setAutoRotate(false);
        });

        let framedDistance = 3;
        const size = { x: 1, y: 1, z: 1 };
        const resetView = () => {
          camera.position.set(0, size.y * 0.04, framedDistance);
          controls.target.set(0, 0, 0);
          controls.update();
        };
        const resize = () => {
          const width = Math.max(1, host.clientWidth);
          const height = Math.max(1, host.clientHeight);
          renderer.setSize(width, height, false);
          camera.aspect = width / height;
          camera.updateProjectionMatrix();
        };
        resize();
        const observer = new ResizeObserver(resize);
        observer.observe(host);

        let cleaned = false;
        cleanup = () => {
          if (cleaned) return;
          cleaned = true;
          renderer.setAnimationLoop(null);
          observer.disconnect();
          controls.dispose();
          scene.traverse((object) => {
            const mesh = object as InstanceType<typeof THREE.Mesh>;
            if (!mesh.isMesh) return;
            mesh.geometry.dispose();
            const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
            for (const material of materials) {
              for (const value of Object.values(material)) {
                if (value instanceof THREE.Texture) value.dispose();
              }
              material.dispose();
            }
          });
          environment.dispose();
          pmrem.dispose();
          renderer.dispose();
          renderer.forceContextLoss();
          renderer.domElement.remove();
          handleRef.current = null;
        };

        const loader = new GLTFLoader();
        const gltf = await loader.loadAsync(url, (event) => {
          if (!disposed && event.total > 0) setProgress(Math.min(1, event.loaded / event.total));
        });
        if (disposed) {
          cleanup?.();
          return;
        }

        const model = gltf.scene;
        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        const extent = box.getSize(new THREE.Vector3());
        model.position.sub(center);
        scene.add(model);
        size.x = extent.x || 1;
        size.y = extent.y || 1;
        size.z = extent.z || 1;
        framedDistance = fitDistance(size, camera.fov, camera.aspect);
        const limits = zoomLimits(framedDistance);
        controls.minDistance = limits.min;
        controls.maxDistance = limits.max;
        camera.near = framedDistance / 100;
        camera.far = framedDistance * 20;
        camera.updateProjectionMatrix();
        resetView();

        handleRef.current = {
          resetView,
          setAutoRotate: (enabled) => {
            controls.autoRotate = enabled;
          },
        };
        setState('ready');

        renderer.setAnimationLoop(() => {
          controls.update();
          renderer.render(scene, camera);
        });
      } catch {
        if (!disposed) setState('error');
      }
    })();

    return () => {
      disposed = true;
      cleanup?.();
    };
  }, [url]);

  const toggleRotate = useCallback(() => {
    const next = !autoRotate;
    setAutoRotate(next);
    handleRef.current?.setAutoRotate(next);
  }, [autoRotate]);

  return (
    <div className="model3d-viewer">
      <div ref={hostRef} className="model3d-stage" aria-label={t('character.model3dStage')} />
      {state === 'loading' && (
        <div className="model3d-overlay" role="status">
          <span className="spinner" />
          <span>
            {t('character.model3dLoading')}
            {progress > 0 ? ` ${Math.round(progress * 100)}%` : ''}
          </span>
        </div>
      )}
      {(state === 'error' || state === 'unsupported') && (
        <div className="model3d-overlay" role="alert">
          {state === 'unsupported'
            ? t('character.model3dUnsupported')
            : t('character.model3dError')}
        </div>
      )}
      {state === 'ready' && (
        <>
          <p className="model3d-hint">{t('character.model3dHint')}</p>
          <div className="model3d-toolbar">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => handleRef.current?.resetView()}
            >
              {t('character.model3dReset')}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              aria-pressed={autoRotate}
              onClick={toggleRotate}
            >
              {t('character.model3dAutoRotate')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
